<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <h3 class="detail-page-title mb-0">{{ client.name }}</h3>
          <div class="detail-page-actions">
            <button v-if="helper.checkAccess('edit_clients') != -1" @click="editRow" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
              {{$t('edit')}}
            </button>
            <button v-if="helper.checkAccess('delete_clients') != -1" @click="deleteRow" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--danger">
              <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
              {{$t('delete')}}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Client Info -->
      <div v-else-if="client && client.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_quotes') != -1">
            <a class="nav-link" :class="{active: step === 'quote'}" @click="step = 'quote'">{{$t('quote')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_orders') != -1">
            <a class="nav-link" :class="{active: step === 'orders'}" @click="step = 'orders'">{{$t('orders')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_deliveries') != -1">
            <a class="nav-link" :class="{active: step === 'deliveries'}" @click="step = 'deliveries'">{{$t('deliveries')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_invoices') != -1">
            <a class="nav-link" :class="{active: step === 'invoices'}" @click="step = 'invoices'">{{$t('invoices')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_returns') != -1">
            <a class="nav-link" :class="{active: step === 'returns'}" @click="step = 'returns'">{{$t('returns')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_payments') != -1">
            <a class="nav-link" :class="{active: step === 'payments'}" @click="step = 'payments'">{{$t('payments')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_contracts') != -1">
            <a class="nav-link" :class="{active: step === 'contracts'}" @click="step = 'contracts'">{{$t('contracts')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row g-4 align-items-start">
            <!-- Left Column: Simple Sales Insights -->
            <div class="col-lg-8">
              <div class="card client-insights-card">
                <div class="card-header bg-white client-insights-header">
                  <div>
                    <h6 class="mb-1"><AppIcon name="bi-bar-chart-line" class="bi bi-bar-chart-line me-2" />{{$t('sales_insights')}}</h6>
                    <small class="text-muted">{{$t('period')}}: {{formattedInsightsPeriod}}</small>
                  </div>
                  <div class="client-insights-filters">
                    <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm" :aria-label="$t('filter_by_date')">
                      <option value="all_time">{{$t('all_time')}}</option>
                      <option value="today">{{$t('today')}}</option>
                      <option value="this_month">{{$t('this_month')}}</option>
                      <option value="last_30_days">{{$t('last_30_days')}}</option>
                      <option value="custom">{{$t('custom_date')}}</option>
                    </select>
                    <date-picker
                      v-if="dateRangeOption === 'custom'"
                      v-model="range"
                      type="date"
                      range
                      :clearable="true"
                      :editable="false"
                      :multi-calendars="1"
                      :placeholder="$t('select_date_range')"
                      @change="handleCustomRangeChange"
                      class="client-insights-date-picker"
                    ></date-picker>
                    <button type="button" class="btn btn-primary btn-sm text-nowrap" :disabled="insightsLoading" @click="downloadSalesReport">
                      <AppIcon name="bi-download" class="bi bi-download me-1" />{{$t('download_report')}}
                    </button>
                  </div>
                </div>

                <div class="card-body">
                  <div v-if="insightsLoading" class="client-insights-loading">
                    <div class="spinner-border spinner-border-sm" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>

                  <div class="row g-2 mb-3">
                    <div class="col-md-4">
                      <div class="client-metric client-metric--primary">
                        <span class="client-metric-icon"><AppIcon name="bi-cash-stack" class="bi bi-cash-stack" /></span>
                        <div>
                          <small>{{$t('total_sales')}}</small>
                          <strong>{{formatMoney(salesInsights.total_sales)}}</strong>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="client-metric client-metric--success">
                        <span class="client-metric-icon"><AppIcon name="bi-check-circle" class="bi bi-check-circle" /></span>
                        <div>
                          <small>{{$t('amount_paid')}}</small>
                          <strong>{{formatMoney(salesInsights.amount_paid)}}</strong>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="client-metric client-metric--warning">
                        <span class="client-metric-icon"><AppIcon name="bi-hourglass-split" class="bi bi-hourglass-split" /></span>
                        <div>
                          <small>{{$t('remaining')}}</small>
                          <strong>{{formatMoney(salesInsights.remaining)}}</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">{{$t('sales_list')}}</h6>
                    <span class="badge bg-light text-dark">{{salesInsights.total_sales_count}} {{$t('sales')}}</span>
                  </div>
                  <div class="client-sales-list">
                    <table class="table client-sales-table mb-0">
                      <thead>
                        <tr>
                          <th scope="col" class="client-sales-reference-heading">
                            <span>{{$t('reference')}}</span>
                            <small>{{$t('date')}}</small>
                          </th>
                          <th scope="col">{{$t('products')}}</th>
                          <th scope="col" class="text-center">{{$t('status')}}</th>
                          <th scope="col" class="text-center">{{$t('total')}}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="sale in salesInsights.sales" :key="sale.id">
                          <td class="client-sale-table-reference">
                            <div class="client-sale-table-reference-value">{{sale.reference || '-'}}</div>
                            <div class="client-sale-table-date">{{formatDate(sale.date)}}</div>
                          </td>
                          <td class="client-sale-table-products">
                            <template v-if="sale.products && sale.products.length">
                              <div v-for="(product, productIndex) in sale.products" :key="`${sale.id}-${productIndex}`" class="client-sale-table-product">
                                <div>
                                  <strong>{{product.label}}</strong>
                                  <small>{{formatQuantity(product.quantity)}} {{product.unity || $t('units')}} × {{formatMoney(product.unit_price)}}</small>
                                </div>
                              </div>
                            </template>
                            <span v-else class="text-muted">{{$t('no_products')}}</span>
                          </td>
                          <td class="text-center"><span class="badge bg-light text-dark border">{{$t(sale.status)}}</span></td>
                          <td class="client-sale-table-money">{{formatMoney(sale.total)}}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="!salesInsights.sales.length" class="client-sales-empty">
                    <AppIcon name="bi-receipt" class="bi bi-receipt" />
                    <p class="mb-0">{{$t('no_sales_for_period')}}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column: Essential Client Information -->
            <div class="col-lg-4">
              <div class="card client-side-card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-person" class="bi bi-person me-2" />{{$t('client_information')}}</h6>
                </div>
                <div class="card-body">
                  <dl class="client-info-list mb-0">
                    <div>
                      <dt>{{$t('client')}}</dt>
                      <dd class="client-info-identity">
                        <span>{{client.name || '-'}}</span>
                        <small v-if="client.reference">{{client.reference}}</small>
                      </dd>
                    </div>
                    <div v-if="client.phone"><dt>{{$t('phone')}}</dt><dd>{{client.phone}}</dd></div>
                    <div v-if="client.email"><dt>{{$t('email')}}</dt><dd>{{client.email}}</dd></div>
                    <div v-if="clientFullAddress"><dt>{{$t('address')}}</dt><dd>{{clientFullAddress}}</dd></div>
                    <div v-if="clientCurrencyEnabled"><dt>{{$t('currency')}}</dt><dd>{{client.currency || business.currency}}</dd></div>
                    <div v-if="client.tax_id"><dt>{{$t('tax_id')}}</dt><dd>{{client.tax_id}}</dd></div>
                    <div v-if="client.vat_number"><dt>{{$t('vat_number')}}</dt><dd>{{client.vat_number}}</dd></div>
                  </dl>
                  <div v-if="client.notes" class="client-notes">
                    <small class="text-muted d-block mb-1">{{$t('notes')}}</small>
                    <p class="mb-0">{{client.notes}}</p>
                </div>
              </div>

              <div class="card mt-4 client-side-card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-wallet2" class="bi bi-wallet2 me-2" />{{$t('balance')}}</h6>
                </div>
                <div class="card-body">
                  <div class="client-balance-chart">
                    <canvas ref="balanceChart" aria-label="Client Balance Chart" role="img"></canvas>
                  </div>
                  <div class="client-balance-totals">
                    <div>
                      <small>{{$t('total')}}</small>
                      <strong>{{formatMoney(total)}}</strong>
                    </div>
                    <div>
                      <small>{{$t('amount_paid')}}</small>
                      <strong class="text-success">{{formatMoney(amount_paid)}}</strong>
                    </div>
                    <div>
                      <small>{{$t('remaining')}}</small>
                      <strong :class="total - amount_paid > 0 ? 'text-danger' : 'text-success'">{{formatMoney(total - amount_paid)}}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'quote' && helper.checkAccess('view_quotes') != -1">
          <quote-component v-if="client && client.id" :client_id="this.client.id" :client="client" :isDetailClient="true"></quote-component>
        </div>

        <div v-if="step === 'orders' && helper.checkAccess('view_orders') != -1">
          <order-component v-if="client && client.id" :client_id="this.client.id" :client="client" :isDetailClient="true"></order-component>
        </div>

        <div v-if="step === 'deliveries' && helper.checkAccess('view_deliveries') != -1">
          <delivery-component v-if="client && client.id" :client_id="this.client.id" :client="client" :isDetailClient="true"></delivery-component>
        </div>

        <div v-if="step === 'invoices' && helper.checkAccess('view_invoices') != -1">
          <invoice-component v-if="client && client.id" :client_id="this.client.id" :client="client" :isDetailClient="true"></invoice-component>
        </div>

        <div v-if="step === 'returns' && helper.checkAccess('view_returns') != -1">
          <return-component v-if="client && client.id" :client_id="this.client.id" :client="client" :isDetailClient="true"></return-component>
        </div>

        <div v-if="step === 'credit_invoices' && helper.checkAccess('view_credit_invoices') != -1">
          <credit-invoice-component v-if="client && client.id" :client="client" :client_id="this.client.id" :isDetailClient="true"></credit-invoice-component>
        </div>

        <div v-if="step === 'payments' && helper.checkAccess('view_payments') != -1">
          <payment-component v-if="client && client.id" :client_id="client.id" :isDetailSupplier="true"></payment-component>
        </div>

        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments :entity_id="client.id" entity_type="client" />
        </div>

        <div v-if="step === 'contracts' && helper.checkAccess('view_contracts') != -1">
          <contract-component v-if="client && client.id" :client_id="client.id" :client="client" :isDetailClient="true"></contract-component>
        </div>
      </div>
    </div>


        <!-- Edit Modal -->
        <div class="modal fade" id="client-edit-client" tabindex="-1" aria-labelledby="client-edit-client" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">{{$t('edit_client')}}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                </div>
                <div class="modal-body" style="text-align:left">
                    <div class="row">
                        <div class="col-sm-6 mb-3">
                            <label for="labelInput" class="form-label">{{$t('label')}}</label>
                            <input v-model="client.name"  type="string" class="form-control" id="labelInput" :placeholder="$t('label')">
                        </div>
                        <div class="col-sm-6 mb-3">
                            <label for="emailInput" class="form-label">{{$t('email')}}</label>
                            <input v-model="client.email"  type="string" class="form-control" id="emailInput" :placeholder="$t('email')">
                        </div>
                        <div class="col-sm-6 mb-3">
                            <label for="phoneInput" class="form-label">{{$t('phone')}}</label>
                            <input v-model="client.phone"  type="string" class="form-control" id="phoneInput" placeholder="06....">
                        </div>
                        <div class="col-sm-6 mb-3">
                            <label for="cityInput" class="form-label">{{$t('city')}}</label>
                            <input v-model="client.city" type="string" class="form-control" id="cityInput" :placeholder="$t('city')">
                        </div>
                        <div class="col-sm-6 mb-3">
                            <label for="stateInput" class="form-label">{{$t('state')}}</label>
                            <input v-model="client.state" type="string" class="form-control" id="stateInput" :placeholder="$t('state')">
                        </div>
                        <div class="col-sm-6 mb-3">
                            <label for="zipCodeInput" class="form-label">{{$t('zip_code')}}</label>
                            <input v-model="client.zip_code" type="string" class="form-control" id="zipCodeInput" :placeholder="$t('zip_code')">
                        </div>
                        <div class="col-sm-6 mb-3">
                            <label for="countryInput" class="form-label">{{$t('country')}}</label>
                            <input v-model="client.country" type="string" class="form-control" id="countryInput" :placeholder="$t('country')">
                        </div>
                        <div v-if="clientCurrencyEnabled" class="col-sm-6 mb-3">
                            <label for="clientCurrencyInput" class="form-label">{{$t('currency')}}</label>
                            <select v-model="client.currency" class="form-select" id="clientCurrencyInput">
                                <option value="">{{$t('default')}} ({{business.currency}})</option>
                                <option v-for="option in currencyOptions" :key="option.value" :value="option.value">
                                    {{option.label}}
                                </option>
                            </select>
                        </div>
                        <div class="col-sm-6 mb-3">
                            <label for="taxIdInput" class="form-label">{{$t('tax_id')}}</label>
                            <input v-model="client.tax_id" type="string" class="form-control" id="taxIdInput" :placeholder="$t('tax_id')">
                        </div>
                        <div class="col-sm-6 mb-3">
                            <label for="vatNumberInput" class="form-label">{{$t('vat_number')}}</label>
                            <input v-model="client.vat_number" type="string" class="form-control" id="vatNumberInput" :placeholder="$t('vat_number')">
                        </div>

                        <div class="col-sm-6 mb-3">
                            <label for="addressInput" class="form-label">{{$t('address')}}</label>
                            <textarea v-model="client.address" class="form-control" id="addressInput" rows="1"></textarea>
                        </div>
                        <div class="col-sm-6 mb-3">
                            <collapsible-notes-field v-model="client.notes" id="client-details-notes" rows="1" />
                        </div>
                        
                    </div>

                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{$t('close')}}</button>
                    <button type="button" class="btn btn-success" :disabled="loading" @click="updateRow"> <AppIcon name="bi-pencil" class="bi bi-pencil me-2" style="font-size:12px" />{{$t('edit')}}</button>
                </div>
                </div>
            </div>
        </div>
        <!-- Edit modal -->

            
    </div>

</template>

<script>

import helper from '../../helper'
import OrderComponent from '../../components/OrderComponent.vue'
import QuoteComponent from '../../components/QuoteComponent.vue'
import InvoiceComponent from '../../components/InvoiceComponent.vue'
import DeliveryComponent from '../../components/DeliveryComponent.vue'
import ReturnComponent from '../../components/ReturnComponent.vue'
import CreditInvoiceComponent from '../../components/CreditInvoiceComponent.vue'
import attachments from '../../components/Attachment.vue'
import PaymentComponent from '../../components/PaymentComponent.vue';
import ContractComponent from '../../components/ContractComponent.vue';
import Chart from 'chart.js'
import DatePicker from 'vue2-datepicker'
import moment from 'moment'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import 'vue2-datepicker/index.css'
import { CURRENCY_OPTIONS } from '../../constants/currencies'

export default {
  name: 'ClientDetails',
  components :{
    OrderComponent,
    QuoteComponent,
    InvoiceComponent,
    DeliveryComponent,
    ReturnComponent,
    CreditInvoiceComponent,
    attachments,
    PaymentComponent,
    ContractComponent,
    DatePicker
  },
  data(){
    return {
        client : {},
        total : 0,
        amount_paid : 0,
        salesInsights: {
          total_sales: 0,
          amount_paid: 0,
          remaining: 0,
          total_sales_count: 0,
          sales: []
        },
        salesSource: 'all',
        dateRangeOption: 'this_month',
        range: [moment().startOf('month').toDate(), new Date()],
        start: moment().startOf('month').format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
        insightsLoading: false,
        insightsRequestId: 0,
        step : 'overview',
        balanceChart: null,
        loading: false
    }
  },
  mounted(){
    window.mixpanel.track("client_details");
    //check permissions
    if(helper.checkAccess('view_clients') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_clients') == -2) return this.$router.push({path : '/subscribe'})

    
    // eslint-disable-next-line no-undef
    this.editModal = new bootstrap.Modal(document.getElementById('client-edit-client'),{backdrop: 'static', keyboard: false})
    
  },
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/clients')
    this.getClient(this.$route.params.id)
    this.fetchSalesInsights()
  },
  beforeDestroy(){
    if(this.balanceChart){
      this.balanceChart.destroy()
      this.balanceChart = null
    }
  },
  watch: {
    step(newVal) {
      if(newVal === 'overview'){
        this.$nextTick(() => this.buildBalanceChart())
      }
    }
  },
    methods : {
        async getClient(id){
        try{
            this.loading = true
            const response = await helper.customGet(`${process.env.VUE_APP_API}/clients/${id}`)
            this.client = response.data.client
            this.client.currency = this.client.currency || ''
            this.total = Number(response.data.total) || 0
            this.amount_paid = Number(response.data.amount_paid) || 0
            this.loading = false
            this.$nextTick(() => this.buildBalanceChart())
        }catch(error){
            this.$router.push({path : '/clients'})
            this.loading = false
        }
        },
        async fetchSalesInsights(){
            const requestId = ++this.insightsRequestId
            this.insightsLoading = true

            try{
                const params = new URLSearchParams()
                if(this.start && this.end){
                    params.append('start', this.start)
                    params.append('end', this.end)
                }
                if(this.salesSource !== 'all'){
                    params.append('source', this.salesSource)
                }
                const query = params.toString() ? `?${params.toString()}` : ''
                const response = await helper.customGet(`${process.env.VUE_APP_API}/clients/${this.$route.params.id}/sales-insights${query}`)

                if(requestId !== this.insightsRequestId) return

                this.salesInsights = {
                    total_sales: Number(response.data.total_sales) || 0,
                    amount_paid: Number(response.data.amount_paid) || 0,
                    remaining: Number(response.data.remaining) || 0,
                    total_sales_count: Number(response.data.total_sales_count) || 0,
                    sales: Array.isArray(response.data.sales) ? response.data.sales : []
                }
            }catch(error){
                if(requestId === this.insightsRequestId){
                    this.$toast.error(this.$t('failed_to_fetch_data'))
                }
            }finally{
                if(requestId === this.insightsRequestId){
                    this.insightsLoading = false
                }
            }
        },
        handleDateRangeChange(){
            const today = moment()

            if(this.dateRangeOption === 'all_time'){
                this.range = []
                this.start = null
                this.end = null
            }else if(this.dateRangeOption === 'today'){
                this.range = [today.toDate(), today.toDate()]
                this.start = today.format('YYYY-MM-DD')
                this.end = today.format('YYYY-MM-DD')
            }else if(this.dateRangeOption === 'this_month'){
                const monthStart = moment().startOf('month')
                this.range = [monthStart.toDate(), today.toDate()]
                this.start = monthStart.format('YYYY-MM-DD')
                this.end = today.format('YYYY-MM-DD')
            }else if(this.dateRangeOption === 'last_30_days'){
                const periodStart = moment().subtract(29, 'days')
                this.range = [periodStart.toDate(), today.toDate()]
                this.start = periodStart.format('YYYY-MM-DD')
                this.end = today.format('YYYY-MM-DD')
            }else{
                this.range = []
                return
            }

            this.fetchSalesInsights()
        },
        handleCustomRangeChange(range){
            if(!range || !range[0] || !range[1]) return
            this.start = moment(range[0]).format('YYYY-MM-DD')
            this.end = moment(range[1]).format('YYYY-MM-DD')
            this.fetchSalesInsights()
        },
        formatQuantity(value){
            const number = Number(value) || 0
            return number.toLocaleString(this.$i18n.locale, {maximumFractionDigits: 3})
        },
        downloadSalesReport(){
            const doc = new jsPDF({orientation: 'landscape'})
            const reportTitle = String(this.$t('client_sales_report'))
            const clientName = this.client.name || '-'

            const pageWidth = doc.internal.pageSize.getWidth()
            const pageHeight = doc.internal.pageSize.getHeight()
            const margin = 14
            const contentWidth = pageWidth - (margin * 2)
            const summaryGap = 4
            const summaryWidth = (contentWidth - (summaryGap * 2)) / 3
            const summaryItems = [
                {label: this.$t('total_sales'), value: this.formatMoney(this.salesInsights.total_sales)},
                {label: this.$t('amount_paid'), value: this.formatMoney(this.salesInsights.amount_paid)},
                {label: this.$t('remaining'), value: this.formatMoney(this.salesInsights.remaining)}
            ]

            doc.setTextColor(35, 35, 35)
            doc.setFont('helvetica', 'bold')
            doc.setFontSize(16)
            doc.text(reportTitle, margin, 16)
            doc.setDrawColor(210, 210, 210)
            doc.setLineWidth(0.4)
            doc.line(margin, 22, pageWidth - margin, 22)

            doc.setTextColor(70, 70, 70)
            doc.setFont('helvetica', 'normal')
            doc.setFontSize(10)
            doc.text(`${String(this.$t('client'))}: ${clientName}${this.client.reference ? `  ·  ${this.client.reference}` : ''}`, margin, 31)
            doc.text(`${String(this.$t('period'))}: ${this.formattedInsightsPeriod}`, margin, 38)

            summaryItems.forEach((item, index) => {
                const x = margin + (index * (summaryWidth + summaryGap))
                if(index > 0){
                    doc.setDrawColor(220, 220, 220)
                    doc.line(x - (summaryGap / 2), 48, x - (summaryGap / 2), 68)
                }
                doc.setTextColor(105, 105, 105)
                doc.setFont('helvetica', 'normal')
                doc.setFontSize(9)
                doc.text(String(item.label), x + 5, 56)
                doc.setTextColor(35, 35, 35)
                doc.setFont('helvetica', 'bold')
                doc.setFontSize(12)
                doc.text(String(item.value), x + 5, 65)
            })

            doc.setDrawColor(210, 210, 210)
            doc.line(margin, 73, pageWidth - margin, 73)

            doc.setTextColor(35, 35, 35)
            doc.setFont('helvetica', 'bold')
            doc.setFontSize(12)
            doc.text(String(this.$t('sales_list')), margin, 82)
            doc.setFont('helvetica', 'normal')
            doc.setFontSize(9)
            doc.setTextColor(105, 105, 105)
            doc.text(`${this.salesInsights.total_sales_count} ${String(this.$t('sales'))}`, pageWidth - margin, 82, {align: 'right'})

            const tableY = 87

            if(!this.salesInsights.sales.length){
                doc.setDrawColor(220, 220, 220)
                doc.roundedRect(margin, tableY, contentWidth, 28, 2, 2)
                doc.setTextColor(125, 125, 125)
                doc.setFontSize(10)
                doc.text(String(this.$t('no_sales_for_period')), pageWidth / 2, tableY + 16, {align: 'center'})
            }

            if(this.salesInsights.sales.length){
                autoTable(doc, {
                    startY: tableY,
                    head: [[
                        `${String(this.$t('reference'))}\n${String(this.$t('date'))}`,
                        String(this.$t('products')),
                        String(this.$t('status')),
                        String(this.$t('total'))
                    ]],
                    body: this.salesInsights.sales.map(sale => {
                        const products = sale.products && sale.products.length
                            ? sale.products.map(product => {
                                const quantity = `${this.formatQuantity(product.quantity)} ${product.unity || String(this.$t('units'))}`
                                return `${product.label || '-'}\n${quantity} × ${this.formatMoney(product.unit_price)}`
                            }).join('\n\n')
                            : String(this.$t('no_products'))

                        return [
                            `${sale.reference || '-'}\n${this.formatDate(sale.date)}`,
                            products,
                            String(this.$t(sale.status)),
                            this.formatMoney(sale.total)
                        ]
                    }),
                    theme: 'grid',
                    margin: {left: margin, right: margin, bottom: 14},
                    styles: {
                        fontSize: 9,
                        cellPadding: 4,
                        halign: 'center',
                        valign: 'middle',
                        lineColor: [220, 220, 220],
                        lineWidth: 0.2,
                        textColor: [45, 45, 45]
                    },
                    headStyles: {
                        fillColor: [245, 245, 245],
                        textColor: [55, 55, 55],
                        fontStyle: 'bold',
                        halign: 'center'
                    },
                    columnStyles: {
                        0: {cellWidth: 52, fontSize: 8},
                        1: {cellWidth: 125},
                        2: {cellWidth: 35},
                        3: {cellWidth: 45, fontStyle: 'bold'}
                    },
                    didParseCell(data){
                        if(data.section === 'body' && data.column.index === 0){
                            data.cell.styles.fontStyle = 'bold'
                        }
                    }
                })
            }

            const pageCount = doc.getNumberOfPages()
            for(let page = 1; page <= pageCount; page++){
                doc.setPage(page)
                doc.setFont('helvetica', 'normal')
                doc.setFontSize(7)
                doc.setTextColor(125, 125, 125)
                doc.text(`${page} / ${pageCount}`, pageWidth - margin, pageHeight - 6, {align: 'right'})
            }

            const safeClientName = clientName.replace(/[^a-z0-9-_]/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'client'
            const periodName = this.start && this.end ? `${this.start}_${this.end}` : 'all-time'
            doc.save(`${safeClientName}-${this.salesSource}-sales-${periodName}.pdf`)
        },
        buildBalanceChart(){
            const ctx = this.$refs.balanceChart
            if(!ctx) return

            if(this.balanceChart){
                this.balanceChart.destroy()
                this.balanceChart = null
            }

            const total = Number(this.total) || 0
            const paid = Number(this.amount_paid) || 0
            const remaining = Math.max(total - paid, 0)
            const hasData = paid + remaining > 0

            this.balanceChart = new Chart(ctx, {
                type: 'pie',
                data: {
                    labels: [this.$t('amount_paid'), this.$t('remaining')],
                    datasets: [{
                        data: hasData ? [paid, remaining] : [1, 0],
                        backgroundColor: ['#34c38f', '#ff6b6b'],
                        borderColor: ['#ffffff', '#ffffff'],
                        borderWidth: 2
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: {
                        position: 'bottom',
                        labels: {fontSize: 13, padding: 16}
                    },
                    tooltips: {
                        callbacks: {
                            label: (tooltipItem, chartData) => {
                                const index = tooltipItem.index
                                return `${chartData.labels[index]}: ${this.formatMoney(chartData.datasets[0].data[index])}`
                            }
                        }
                    }
                }
            })
        },
        editRow(){
            this.editModal.show()
        },
        async deleteRow(){
            const result = await this.$swal.fire({
                title: `${this.$t('are_you_sure')}?`,
                text : this.$t('you_cant_revert'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('yes_delete_it')
            })
            if (result.isConfirmed) {
                await helper.customDelete(`${process.env.VUE_APP_API}/clients/${this.client.id}`)
                this.$router.push({path : '/clients'})
            }
        },
        async updateRow(){
            try{
                this.loading = true
                await helper.customPatch(`${process.env.VUE_APP_API}/clients/${this.client.id}`,this.client)
                this.loading = false
                this.editModal.hide()
                this.getClient(this.$route.params.id)
            }catch(error){
                this.loading = false
            }
        },
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) {
            const currency = this.clientCurrencyEnabled
                ? (this.client.currency || this.business.currency)
                : this.business.currency
            return helper.formatMoney(number, currency)
        }
    },
    computed : {
        business(){ return this.$store.getters.BUSINESS},
        formattedInsightsPeriod(){
            if(!this.start || !this.end) return this.$t('all_time')
            return `${moment(this.start).format('DD/MM/YYYY')} - ${moment(this.end).format('DD/MM/YYYY')}`
        },
        clientFullAddress(){
            return [this.client.address, this.client.city, this.client.state, this.client.zip_code, this.client.country]
                .filter(Boolean)
                .join(', ')
        },
        clientCurrencyEnabled(){
            return !!this.business?.metadata?.use_client_currency_for_invoices
        },
        currencyOptions(){ return CURRENCY_OPTIONS },
        helper(){ return helper },
    }
}
</script>

<style scoped>
.client-insights-card,
.client-side-card {
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(20, 35, 55, 0.04);
  overflow: hidden;
}

.client-insights-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
}

.client-insights-filters {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 8px;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: thin;
}

.client-insights-filters .form-select {
  width: 145px;
  min-width: 145px;
  min-height: 36px;
  font-size: 0.9rem;
}

.client-insights-date-picker {
  width: 205px;
  min-width: 205px;
}

.client-insights-filters .btn {
  flex: 0 0 auto;
  min-height: 36px;
  font-size: 0.9rem;
}

::v-deep .client-insights-date-picker .mx-input {
  height: 36px;
  font-size: 0.9rem;
}

.client-insights-card .card-body {
  position: relative;
}

.client-insights-loading {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.7);
}

.client-metric {
  min-height: 70px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e9edf3;
  border-radius: 8px;
  background: #fbfcfe;
}

.client-metric-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 1rem;
}

.client-metric small,
.client-metric strong,
.client-metric span:not(.client-metric-icon) {
  display: block;
}

.client-metric small {
  color: #6c757d;
  margin-bottom: 1px;
  font-size: 0.78rem;
}

.client-metric strong {
  color: #25364b;
  font-size: 1rem;
  line-height: 1.25;
}

.client-metric span:not(.client-metric-icon) {
  margin-top: 2px;
  color: #8a96a6;
  font-size: 0.75rem;
}

.client-metric--primary .client-metric-icon { color: #3568d4; background: #eaf0ff; }
.client-metric--success .client-metric-icon { color: #1f9d67; background: #e8f7f0; }
.client-metric--warning .client-metric-icon { color: #d3831f; background: #fff3df; }
.client-sales-list {
  max-height: 560px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow-y: auto;
}

.client-sales-table {
  min-width: 780px;
  border-collapse: separate;
  border-spacing: 0;
  color: #2d3b4f;
  font-size: 0.84rem;
}

.client-sales-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 11px 14px;
  border-bottom: 1px solid #ced4da;
  background: #f8f9fa;
  color: #5d6878;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}

.client-sales-reference-heading span,
.client-sales-reference-heading small {
  display: block;
}

.client-sales-reference-heading small {
  margin-top: 1px;
  color: #7b8797;
  font-size: 0.68rem;
  font-weight: 500;
}

.client-sales-table tbody td {
  padding: 14px 16px;
  border-color: #e9ecef;
  text-align: center;
  vertical-align: middle;
}

.client-sales-table-reference {
  width: 20%;
  white-space: nowrap;
}

.client-sale-table-reference-value {
  display: block;
  width: 100%;
  color: #26374d;
  font-size: 0.75rem;
  font-weight: 600;
}

.client-sale-table-date {
  display: block;
  width: 100%;
  margin-top: 3px;
  color: #7b8797;
  font-size: 0.68rem;
  font-weight: 400;
}

.client-sale-table-products {
  width: 52%;
}

.client-sale-table-product {
  display: block;
  text-align: center;
}

.client-sale-table-product + .client-sale-table-product {
  padding-top: 7px;
  margin-top: 7px;
  border-top: 1px solid #edf0f3;
}

.client-sale-table-product strong,
.client-sale-table-product small {
  display: block;
}

.client-sale-table-product strong {
  font-size: 0.84rem;
}

.client-sale-table-product small {
  margin-top: 2px;
  color: #6f7d90;
  font-size: 0.75rem;
}

.client-sale-table-money {
  font-weight: 600;
  white-space: nowrap;
  text-align: center;
}

.client-sales-empty {
  min-height: 190px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border: 1px dashed #dce2ea;
  border-radius: 9px;
  color: #8a96a6;
}

.client-sales-empty i {
  font-size: 2rem;
}

.client-side-card .card-header {
  padding: 11px 14px;
  border-bottom: 1px solid #edf0f4;
}

.client-side-card .card-body {
  padding: 10px 14px 14px;
}

.client-info-list > div {
  display: grid;
  grid-template-columns: minmax(80px, 0.7fr) minmax(0, 1.3fr);
  align-items: baseline;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f2f5;
}

.client-info-list dt,
.client-info-list dd {
  margin: 0;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
}

.client-info-list dt {
  color: #7b8797;
  font-weight: 500;
}

.client-info-list dd {
  color: #2d3b4f;
  font-weight: 600;
  text-align: end;
}

.client-info-identity {
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.client-info-identity span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.client-info-identity small {
  flex: 0 0 auto;
  color: #8a96a6;
  font-size: 0.72rem;
  font-weight: 500;
}

.client-notes {
  padding: 9px 10px;
  margin-top: 10px;
  border-radius: 8px;
  background: #f7f9fc;
  font-size: 0.82rem;
}

.client-balance-chart {
  position: relative;
  height: 160px;
  margin-bottom: 14px;
}

.client-balance-totals {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding-top: 14px;
  border-top: 1px solid #edf0f4;
  text-align: center;
}

.client-balance-totals > div + div {
  border-inline-start: 1px solid #edf0f4;
}

.client-balance-totals small,
.client-balance-totals strong {
  display: block;
}

.client-balance-totals small {
  margin-bottom: 4px;
  color: #6f7d90;
  font-size: 0.78rem;
}

.client-balance-totals strong {
  padding: 0 4px;
  font-size: 0.88rem;
  overflow-wrap: anywhere;
}

@media (max-width: 1399.98px) {
  .client-insights-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .client-insights-filters {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 575.98px) {
  .client-insights-filters {
    flex-wrap: wrap;
    overflow: visible;
  }

  .client-insights-date-picker,
  .client-insights-filters .form-select,
  .client-insights-filters .btn {
    width: 100%;
  }

}
</style>
